import React, { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";

import { DashboardLayout } from "@/components/layout";
import {
  Card,
  CardContent,
  ConfirmationModal,
  Input,
  PageHeader,
  Switch,
  showToast,
} from "@/components/ui";
import {
  type LeavesQuery,
  useLeavesQuery,
  useSetLeafFlagsMutation,
} from "@/graphql/generated";
import { asksBeforeRequired, filterLeaves } from "@/lib/leaves/leafRules";

type Leaf = LeavesQuery["leaves"][number];

const typesLabel = (count: number) =>
  `${count} ${count === 1 ? "type" : "types"}`;

export default function Leaves() {
  const queryClient = useQueryClient();
  const { data } = useLeavesQuery();
  const [text, setText] = useState("");
  const [folder, setFolder] = useState("");
  const [asking, setAsking] = useState<Leaf | null>(null);
  const setFlags = useSetLeafFlagsMutation({
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: useLeavesQuery.getKey() }),
    onError: (error) =>
      showToast.error(
        "Update failed",
        error instanceof Error ? error.message : "Please try again.",
      ),
  });
  const leaves = data?.leaves ?? [];
  const folders = Array.from(new Set(leaves.map((leaf) => leaf.folder))).sort();
  const shown = filterLeaves(leaves, folder, text);
  const setRequired = (leaf: Leaf, next: boolean) =>
    asksBeforeRequired(next, leaf.usedBy)
      ? setAsking(leaf)
      : setFlags.mutate({ path: leaf.path, alwaysRequired: next });

  return (
    <DashboardLayout activePath="/catalog/leaves">
      <PageHeader
        title="Leaves"
        subtitle="Required and Per store for every leaf"
        breadcrumbs={[{ label: "Leaves", active: true }]}
      />
      <div className="flex gap-2 mb-3 max-w-xl">
        <Input
          aria-label="Filter leaves"
          value={text}
          onChange={(e) => setText(e.target.value)}
        />
        <select
          aria-label="Folder"
          value={folder}
          onChange={(e) => setFolder(e.target.value)}
        >
          <option value="">all</option>
          {folders.map((f) => (
            <option key={f} value={f}>
              {f}
            </option>
          ))}
        </select>
      </div>
      <Card>
        <CardContent className="p-0">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-slate-500">
                <th className="p-3">Leaf</th>
                <th>Folder</th>
                <th>Used by</th>
                <th>Required</th>
                <th>Per store</th>
              </tr>
            </thead>
            <tbody>
              {shown.map((leaf) => (
                <tr
                  key={leaf.path}
                  className="border-t border-slate-200 dark:border-slate-800"
                >
                  <td className="p-3 font-mono">
                    {leaf.leaf}
                    {leaf.anchor && (
                      <span className="ml-2 text-xs text-indigo-600">
                        anchor
                      </span>
                    )}
                  </td>
                  <td>{leaf.folder}</td>
                  <td>{typesLabel(leaf.usedBy)}</td>
                  <td>
                    <Switch
                      checked={leaf.alwaysRequired}
                      disabled={setFlags.isPending}
                      aria-label={`Required: ${leaf.leaf}`}
                      onCheckedChange={(next) => setRequired(leaf, next)}
                    />
                  </td>
                  <td>
                    <Switch
                      checked={leaf.perStore}
                      disabled={setFlags.isPending}
                      aria-label={`Per store: ${leaf.leaf}`}
                      onCheckedChange={(next) =>
                        setFlags.mutate({ path: leaf.path, perStore: next })
                      }
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>
      <ConfirmationModal
        open={asking !== null}
        onOpenChange={(open) => !open && setAsking(null)}
        title={`Make ${asking?.leaf ?? ""} required?`}
        description={`Making ${asking?.leaf} required affects every seller form that uses it (used by ${typesLabel(asking?.usedBy)}). Continue?`}
        confirmLabel="Continue"
        onConfirm={() => {
          if (asking)
            setFlags.mutate({ path: asking.path, alwaysRequired: true });
          setAsking(null);
        }}
      />
    </DashboardLayout>
  );
}
